import { useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, Check } from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { VoiceoverPlayer } from "@/components/site/VoiceoverPlayer";
import { Button } from "@/components/ui/button";
import { products } from "@/data/products";
import { productDetails } from "@/data/productDetails";
import { SITE_URL, productPath, seoForPath, useSeo } from "@/lib/seo";
import NotFound from "./NotFound";

const renderBenefit = (b: string) => {
  const match = b.match(/^\*\*(.+?)\*\*\s*(.*)$/);
  return match ? (
    <>
      <span className="font-semibold text-foreground">{match[1]}</span> {match[2]}
    </>
  ) : (
    b
  );
};

const ProductContent = ({ slug }: { slug: string }) => {
  const product = products.find((p) => p.slug === slug)!;
  const details = productDetails[slug];
  useSeo(seoForPath(productPath(slug)));
  const others = products.filter((p) => p.slug !== slug);

  const url = `${SITE_URL}${productPath(slug)}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `${product.name}®`,
      description: product.metaDescription,
      applicationCategory: "BusinessApplication",
      applicationSubCategory: product.tagline,
      operatingSystem: "Linux, Windows, Docker, Cloud",
      url,
      image: `${SITE_URL}/images/products/${product.slug}.png`,
      featureList: [
        ...product.benefits.map((b) => b.replace(/\*\*/g, "")),
        ...details.specs.flatMap((s) => s.items),
      ],
      keywords: details.useCases.map((u) => u.title).join(", "),
      additionalProperty: details.metrics.map((m) => ({ "@type": "PropertyValue", name: m.label, value: m.value })),
      provider: { "@id": "https://banetech.net/#organization" },
      publisher: { "@type": "Organization", name: "Bane Tech", url: SITE_URL },
      offers: { "@type": "Offer", url: `${SITE_URL}/#contact`, description: "Pricing on request. Request a demo" },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Bane Tech", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Products", item: `${SITE_URL}/#products` },
        { "@type": "ListItem", position: 3, name: `${product.name}®`, item: url },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />
      <main>
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-24 bg-hero-network">
          <div className="container max-w-4xl">
            <div className="border-b border-border/70 pb-5 mb-12 md:mb-16">
              <a
                href="/#products"
                className="group inline-flex items-center text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ArrowLeft className="mr-2 h-4 w-4 transition-transform duration-200 group-hover:-translate-x-0.5" />
                All products
              </a>
            </div>
            <div className={`inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br ${product.accent} mb-7`}>
              <img src={product.icon} alt={product.alt} width={36} height={36} className="h-9 w-9" />
            </div>
            <h1 className="font-display text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
              {product.name}<span className="text-primary">®</span>
            </h1>
            <p className="mt-4 font-display text-xl md:text-2xl font-medium text-gradient">{product.tagline}</p>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-3xl">{product.problem}</p>
            <div className="mt-10">
              <Button variant="hero" size="xl" asChild>
                <a href={`/?product=${slug}#contact`} data-umami-event="request_demo" data-umami-event-product={slug}>
                  Request Demo <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
            <dl className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px overflow-hidden rounded-2xl border border-border bg-border">
              {details.metrics.map((m) => (
                <div key={m.label} className="bg-card p-5">
                  <dt className="sr-only">{m.label}</dt>
                  <dd className="font-display text-xl md:text-2xl font-bold text-gradient">{m.value}</dd>
                  <dd className="mt-1 text-xs text-muted-foreground">{m.label}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        <section className="py-20 md:py-28">
          <div className="container max-w-4xl">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Key Capabilities</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-10">
              What {product.name}<span className="text-primary">®</span> delivers
            </h2>
            <div className="grid gap-5 md:grid-cols-2">
              {product.benefits.map((b) => (
                <div key={b} className="rounded-2xl border border-border bg-card-gradient p-6 text-sm text-muted-foreground leading-relaxed">
                  {renderBenefit(b)}
                </div>
              ))}
            </div>
            <p className="mt-10 pt-6 border-t border-border font-mono text-xs uppercase tracking-wider text-muted-foreground">
              {product.positioning}
            </p>
          </div>
        </section>

        <section className="py-20 md:py-28 bg-secondary/40 border-y border-border">
          <div className="container max-w-4xl">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Use Cases</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-10">Where it makes the difference</h2>
            <div className="grid gap-5 md:grid-cols-3">
              {details.useCases.map((u, i) => (
                <div key={u.title} className="rounded-2xl border border-border bg-card p-6">
                  <span className="font-mono text-xs text-primary">0{i + 1}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold leading-snug">{u.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{u.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {details.caseStudies && (
          <section className="py-20 md:py-28">
            <div className="container max-w-4xl">
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">In Production</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-4">Proven in production</h2>
              <p className="text-muted-foreground leading-relaxed max-w-3xl mb-10">
                {product.name}® runs mission-critical integration workloads for banking and e-commerce institutions, where volume and latency are non-negotiable.
              </p>
              <div className="grid gap-6">
                {details.caseStudies.map((c, i) => (
                  <article key={c.client} className="rounded-2xl border border-border bg-card-gradient p-7 md:p-10">
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
                      Case Study 0{i + 1} · {c.sector}
                    </p>
                    {c.title && <h3 className="mt-3 font-display text-2xl font-bold leading-snug">{c.title}</h3>}
                    <dl className="mt-6 grid grid-cols-2 gap-6 border-y border-border py-6">
                      <div>
                        <dd className="font-display text-3xl font-bold text-gradient">{c.invocations}</dd>
                        <dt className="mt-1 text-xs text-muted-foreground">Invocations per day</dt>
                      </div>
                      <div>
                        <dd className="font-display text-3xl font-bold text-gradient">{c.latency}</dd>
                        <dt className="mt-1 text-xs text-muted-foreground">Average processing time</dt>
                      </div>
                    </dl>
                    {c.challenge && (
                      <div className="mt-6 grid gap-6 md:grid-cols-3">
                        <div>
                          <h4 className="font-mono text-xs uppercase tracking-widest text-primary mb-2">The Challenge</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">{c.challenge}</p>
                        </div>
                        <div>
                          <h4 className="font-mono text-xs uppercase tracking-widest text-primary mb-2">The Solution</h4>
                          <p className="text-sm text-muted-foreground leading-relaxed">{c.solution}</p>
                        </div>
                        <div>
                          <h4 className="font-mono text-xs uppercase tracking-widest text-primary mb-2">Production Impact</h4>
                          <ul className="space-y-2">
                            {c.results?.map((r) => (
                              <li key={r} className="flex gap-2 text-sm text-muted-foreground">
                                <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                                {r}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        {details.scenarios && (
          <section className="py-20 md:py-28 bg-secondary/40 border-y border-border">
            <div className="container max-w-4xl">
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Scenarios</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-10">Production scenarios</h2>
              <div className="grid gap-5 md:grid-cols-2">
                {details.scenarios.map((s) => (
                  <div key={s.title} className="rounded-2xl border border-border bg-card p-6">
                    <h3 className="font-display text-lg font-semibold">{s.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.text}</p>
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      {s.flow.map((f, j) => (
                        <span key={f} className="flex items-center gap-2">
                          <span className="rounded-lg border border-border bg-background/60 px-3 py-1.5 text-xs">{f}</span>
                          {j < s.flow.length - 1 && <ArrowRight className="h-4 w-4 text-primary" aria-hidden="true" />}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {details.architecture && (
          <section className="py-20 md:py-28 bg-secondary/40 border-y border-border">
            <div className="container max-w-4xl">
              <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Architecture</p>
              <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-10">How data flows through {product.name}®</h2>
              <VoiceoverPlayer src={`/audio/${slug}.mp3`} label={`Listen: ${product.name}® in 45 seconds`} />
              <div className="grid items-stretch gap-4 md:grid-cols-[1fr_auto_1.2fr_auto_1fr]">
                {([
                  ["Sources", details.architecture.ingress, false],
                  ["arrow"],
                  [`${product.name}® Engine`, details.architecture.core, true],
                  ["arrow"],
                  ["Targets", details.architecture.egress, false],
                ] as [string, string[]?, boolean?][]).map(([title, items, core], i) =>
                  title === "arrow" ? (
                    <div key={i} className="flow-connector" aria-hidden="true">
                      <span className="flow-track" />
                      <span className="flow-dot" />
                      <span className="flow-dot" style={{ animationDelay: "0.6s" }} />
                      <span className="flow-dot" style={{ animationDelay: "1.2s" }} />
                    </div>
                  ) : (
                    <div
                      key={i}
                      className={`relative rounded-2xl border p-6 ${core ? "flow-core border-primary/50 bg-hero" : "border-border bg-card"}`}
                    >
                      <h3 className="font-display font-semibold mb-4 flex items-center gap-2">
                        {core && <span className="flow-live" aria-hidden="true" />}
                        {title}
                      </h3>
                      <ul className="space-y-2">
                        {items!.map((it, k) => (
                          <li
                            key={it}
                            className="flow-item rounded-lg border border-border bg-background/60 px-3 py-2 text-sm text-muted-foreground"
                            style={{ animationDelay: `${(k * 0.45 + (core ? 0.9 : title === "Targets" ? 1.8 : 0)).toFixed(2)}s` }}
                          >
                            {it}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                )}
              </div>
            </div>
          </section>
        )}

        <section className="py-20 md:py-28">
          <div className="container max-w-4xl">
            <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">Technical Specifications</p>
            <h2 className="font-display text-3xl md:text-4xl font-bold leading-tight mb-10">Built for enterprise environments</h2>
            <div className="grid gap-8 md:grid-cols-3">
              {details.specs.map((s) => (
                <div key={s.title}>
                  <h3 className="font-display font-semibold pb-3 mb-4 border-b border-border">{s.title}</h3>
                  <ul className="space-y-2.5">
                    {s.items.map((it) => (
                      <li key={it} className="flex gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 mt-0.5 shrink-0 text-primary" />
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="mt-20 rounded-3xl border border-border bg-hero p-8 md:p-12">
              <h2 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                Ready to run {product.name}<span className="text-primary">®</span> in your infrastructure?
              </h2>
              <p className="mt-3 text-muted-foreground max-w-2xl">
                Schedule a technical demo or an architecture session with our engineering team.
              </p>
              <Button variant="hero" size="lg" className="mt-6" asChild>
                <a href={`/?product=${slug}#contact`} data-umami-event="request_demo" data-umami-event-product={slug}>
                  Request Demo <ArrowRight className="ml-2 h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>
        </section>

        <section className="pb-24 md:pb-32">
          <div className="container max-w-4xl">
            <h2 className="font-display text-2xl md:text-3xl font-bold mb-8">Explore the suite</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {others.map((p) => (
                <a
                  key={p.slug}
                  href={productPath(p.slug)}
                  className="group flex items-center gap-4 rounded-xl border border-border bg-card-gradient p-4 transition-all hover:border-primary/40 hover:-translate-y-0.5"
                >
                  <span className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${p.accent}`}>
                    <img src={p.icon} alt={p.alt} width={28} height={28} className="h-7 w-7" />
                  </span>
                  <span className="flex-1 font-display font-semibold">{p.name}®</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                </a>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

const ProductPage = () => {
  const { slug } = useParams();
  if (!slug || !products.some((p) => p.slug === slug)) return <NotFound />;
  return <ProductContent slug={slug} />;
};

export default ProductPage;
